import { useEffect, useRef } from 'react'
import Link from 'next/link'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import { CheckCircle, ExternalLink } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Asset } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/hooks/use-editor-store'
import { useProjectStore } from '@/hooks/use-project-store'

type KlipyGif = {
  id: number
  uuid: string // generated by us because there are duplicates in response, try search "panda" in gif section
  slug: string
  title: string
  blur_preview: string
  file: {
    hd: {
      gif: {
        url: string
        width: number
        height: number
        size: number
      }
      webp: {
        url: string
        width: number
        height: number
        size: number
      }
    }
    md: {
      gif: {
        url: string
        width: number
        height: number
        size: number
      }
      webp: {
        url: string
        width: number
        height: number
        size: number
      }
    }
    sm: {
      gif: {
        url: string
        width: number
        height: number
        size: number
      }
      webp: {
        url: string
        width: number
        height: number
        size: number
      }
    }
    xs: {
      gif: {
        url: string
        width: number
        height: number
        size: number
      }
      webp: {
        url: string
        width: number
        height: number
        size: number
      }
    }
  }
  type: string
}

type KlipySearchResponse = {
  result: boolean
  data: {
    data: KlipyGif[]
    current_page: number
    per_page: number
    has_next: boolean
  }
}

const PER_PAGE = 12

interface GifLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

export const GifLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: GifLibraryProps) => {
  const t = useTranslations('gif-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { setIsToolboxOpen } = useProjectStore()
  const { getIsEditorOn, getIsTargetImage, getIsTargetVideo, setTargetSrc } =
    useEditorStore()
  const isEditorOn = getIsEditorOn()
  const isImageSelectedInEditor = getIsEditorOn() && getIsTargetImage()

  const getKey = (
    pageIndex: number,
    previousPageData: KlipySearchResponse | null,
  ) => {
    if (limit && pageIndex > 0) return null // Only fetch first page if limit is set
    if (previousPageData && !previousPageData.data.data.length) return null
    const searchParam = query ? `&q=${encodeURIComponent(query)}` : ''
    return `/api/media/klipy?type=gifs&page=${pageIndex + 1}&per_page=${limit || PER_PAGE}${searchParam}`
  }

  const { data, size, setSize, isLoading } =
    useSWRInfinite<KlipySearchResponse>(
      getKey,
      async (url) => {
        const res = await fetch(url)
        if (!res.ok) throw new Error('Failed to fetch')
        return res.json()
      },
      {
        revalidateFirstPage: false,
      },
    )

  const allGifs = data ? data.flatMap((page) => page.data.data) : []
  const gifs = limit
    ? allGifs.filter((gif) => gif.file?.sm?.gif?.url).slice(0, limit)
    : allGifs.filter((gif) => gif.file?.sm?.gif?.url)

  for (const gif of gifs) {
    gif.uuid = crypto.randomUUID()
  }

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = !data?.[0] || data[0].data.data.length === 0
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1].data.data.length < PER_PAGE)

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  const handleGifSelect = (e: React.MouseEvent, gif: KlipyGif) => {
    if (isEditorOn && !isImageSelectedInEditor) return

    const assetData: Asset = {
      source: 'elements',
      type: 'gif',
      link: gif.file.hd.gif.url,
      thumbnail: gif.file.sm.gif.url,
    }
    // Always call onSelect first for modal context
    onSelect(e, assetData)

    // Also update editor if needed
    if (isImageSelectedInEditor) {
      setIsToolboxOpen(false)
      setTargetSrc(gif.file.hd.gif.url)
    }
  }

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_gifs')}</p>
      </div>
    )
  }

  return (
    <>
      {gifs.map((gif) => (
        <motion.div
          key={gif.uuid}
          className={cn(
            'flex-none aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group relative cursor-pointer',
            isExpanded ? 'w-fit' : 'w-[80px]',
            isEditorOn && !isImageSelectedInEditor
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
          )}
          onClick={(e) => handleGifSelect(e, gif)}
        >
          <img
            src={gif.file.sm.gif.url}
            alt={gif.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
            <p className="text-xs text-white line-clamp-2">
              {t('dimensions', {
                width: gif.file.hd.gif.width,
                height: gif.file.hd.gif.height,
              })}
            </p>
          </div>
          {selectedAssets.some(
            (asset) => asset.link === gif.file.hd.gif.url,
          ) && (
            <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
              <CheckCircle className="w-4 h-4" />
            </div>
          )}
          <div className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Link
              href={gif.file.hd.gif.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-300 hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
              title={t('open_in_new')}
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </>
  )
}
